import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../../components/EmptyState';

interface Tier {
  id: number;
  shelf_id: number;
  position: number;
  nickname: string;
  barcode: string;
  shelf_name: string;
  location_name: string;
  items_count: string;
  totes_count: string;
}

export default function TiersPage() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [shelves, setShelves] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    shelf_id: '',
    position: '',
    nickname: '',
    barcode: ''
  });

  useEffect(() => {
    fetchTiers();
    fetchShelves();
  }, []);

  const fetchTiers = async () => {
    try {
      const res = await fetch('/api/tiers-pg');
      const json = await res.json();
      setTiers(json.data || []);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShelves = async () => {
    try {
      const res = await fetch('/api/shelves-pg');
      const json = await res.json();
      setShelves(json.data || []);
    } catch (error) {
      console.error('Error fetching shelves:', error);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate shelf_id is selected
    if (!formData.shelf_id) {
      alert('Please select a shelf');
      return;
    }

    try {
      const res = await fetch('/api/tiers-pg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shelf_id: formData.shelf_id,
          position: formData.position ? parseInt(formData.position) : undefined,
          nickname: formData.nickname || undefined,
          barcode: formData.barcode || undefined
        })
      });

      if (res.ok) {
        setShowModal(false);
        setFormData({ shelf_id: '', position: '', nickname: '', barcode: '' });
        fetchTiers();
      } else {
        const error = await res.json();
        alert(error.error || 'Failed to create tier');
      }
    } catch (error) {
      console.error('Error creating tier:', error);
      alert('Failed to create tier');
    }
  };

  const handleEdit = (tier: Tier) => {
    setEditingId(tier.id);
    setFormData({
      shelf_id: String(tier.shelf_id),
      position: String(tier.position),
      nickname: tier.nickname || '',
      barcode: tier.barcode || ''
    });
    setShowModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;

    try {
      const res = await fetch(`/api/tiers-pg/${editingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shelf_id: formData.shelf_id,
          position: formData.position ? parseInt(formData.position) : undefined,
          nickname: formData.nickname || undefined,
          barcode: formData.barcode || undefined
        })
      });

      if (res.ok) {
        setShowModal(false);
        setEditingId(null);
        setFormData({ shelf_id: '', position: '', nickname: '', barcode: '' });
        fetchTiers();
      } else {
        const error = await res.json();
        alert(error.error || 'Failed to update tier');
      }
    } catch (error) {
      console.error('Error updating tier:', error);
      alert('Failed to update tier');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this tier?')) return;

    try {
      const res = await fetch(`/api/tiers-pg/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchTiers();
      } else {
        const error = await res.json();
        alert(error.error || 'Failed to delete tier');
      }
    } catch (error) {
      console.error('Error deleting tier:', error);
      alert('Failed to delete tier');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        color: '#E0E6F0',
        fontFamily: 'Rajdhani, sans-serif'
      }}>
        Loading tiers...
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: 'Rajdhani, sans-serif',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      padding: '24px',
      color: '#E0E6F0'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <h1 style={{
          fontSize: '48px',
          fontWeight: 700,
          color: '#FF6B35',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          margin: 0,
          textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
        }}>
          Tiers Management
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#8A95AA',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginTop: '8px'
        }}>
          Manage vertical shelf tiers - tiers belong to shelves
        </p>

        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <button
            onClick={() => setShowModal(true)}
            style={{
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '2px solid #B91BFF',
              background: 'rgba(185, 27, 255, 0.2)',
              color: '#B91BFF',
              boxShadow: '0 0 20px rgba(185, 27, 255, 0.3)'
            }}
          >
            + Create Tier
          </button>
          <button
            onClick={fetchTiers}
            style={{
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '2px solid rgba(0, 240, 255, 0.4)',
              background: 'rgba(0, 240, 255, 0.1)',
              color: '#00F0FF'
            }}
          >
            ↻ Refresh
          </button>
          <button
            onClick={() => navigate('/admin')}
            style={{
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '2px solid rgba(138, 149, 170, 0.4)',
              background: 'rgba(138, 149, 170, 0.1)',
              color: '#8A95AA'
            }}
          >
            ← Back
          </button>
        </div>
      </div>

      {/* Empty State */}
      {tiers.length === 0 && !loading && (
        <EmptyState
          icon="📊"
          title="No Tiers Found"
          description="No tiers have been created yet. Tiers are vertical divisions within shelves that help organize inventory by height."
          actionLabel="+ Create Your First Tier"
          onAction={() => setShowModal(true)}
          color="#B91BFF"
        />
      )}

      {/* Tier Cards Grid */}
      {tiers.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '16px'
        }}>
          {tiers.map(tier => (
          <div
            key={tier.id}
            style={{
              background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.8), rgba(36, 41, 56, 0.8))',
              border: '2px solid rgba(255, 20, 147, 0.3)',
              borderRadius: '12px',
              padding: '20px'
            }}
          >
            {/* Tier Header */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '1px solid rgba(255, 20, 147, 0.2)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                background: 'rgba(255, 20, 147, 0.2)',
                border: '2px solid #FF1493',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                flexShrink: 0
              }}>
                📊
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#FF1493',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  margin: 0
                }}>
                  Tier {tier.position}
                </h3>
                <p style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#00F0FF',
                  margin: '6px 0 0 0'
                }}>
                  {tier.nickname || 'No nickname'}
                </p>
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              marginBottom: '16px'
            }}>
              <div style={{
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid #00F0FF',
                borderRadius: '6px',
                padding: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '2px', color: '#00F0FF' }}>
                  {tier.position}
                </div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#8A95AA' }}>
                  Position
                </div>
              </div>
              <div style={{
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid #B91BFF',
                borderRadius: '6px',
                padding: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '2px', color: '#B91BFF' }}>
                  {tier.shelf_name || 'N/A'}
                </div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#8A95AA' }}>
                  Shelf
                </div>
              </div>
              <div style={{
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid #FF1493',
                borderRadius: '6px',
                padding: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '2px', color: '#FF1493' }}>
                  {tier.totes_count || 0}
                </div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#8A95AA' }}>
                  Totes
                </div>
              </div>
              <div style={{
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid #6BCF7F',
                borderRadius: '6px',
                padding: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '2px', color: '#6BCF7F' }}>
                  {tier.barcode}
                </div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#8A95AA' }}>
                  Barcode
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              paddingTop: '12px',
              borderTop: '1px solid rgba(255, 20, 147, 0.2)'
            }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleEdit(tier)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '6px',
                    border: '2px solid rgba(185, 27, 255, 0.4)',
                    background: 'rgba(185, 27, 255, 0.1)',
                    color: '#B91BFF',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer'
                  }}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => navigate(`/tiers/${tier.id}`)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '6px',
                    border: '2px solid rgba(0, 240, 255, 0.4)',
                    background: 'rgba(0, 240, 255, 0.1)',
                    color: '#00F0FF',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer'
                  }}
                >
                  👁 View
                </button>
              </div>
              <button
                onClick={() => window.open(`/api/tiers-pg/${tier.id}/label`, '_blank')}
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: '2px solid rgba(107, 207, 127, 0.4)',
                  background: 'rgba(107, 207, 127, 0.1)',
                  color: '#6BCF7F',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: 'pointer'
                }}
              >
                🖨 Print Label
              </button>
              <button
                onClick={() => handleDelete(tier.id)}
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: '2px solid rgba(239, 68, 68, 0.4)',
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#EF4444',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: 'pointer'
                }}
              >
                🗑 Delete
              </button>
            </div>
          </div>
        ))}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95), rgba(36, 41, 56, 0.95))',
            backdropFilter: 'blur(12px)',
            border: '2px solid rgba(255, 20, 147, 0.4)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 0 40px rgba(255, 20, 147, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '2px solid rgba(255, 20, 147, 0.3)'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 700,
                color: '#FF1493',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: 0
              }}>
                {editingId ? 'Edit Tier' : 'Create New Tier'}
              </h3>
              <button
                onClick={() => { setShowModal(false); setEditingId(null); setFormData({ shelf_id: '', position: '', nickname: '', barcode: '' }); }}
                style={{
                  background: 'rgba(138, 149, 170, 0.1)',
                  border: '2px solid rgba(138, 149, 170, 0.4)',
                  borderRadius: '6px',
                  padding: '8px',
                  color: '#8A95AA',
                  cursor: 'pointer',
                  fontSize: '16px',
                  width: '32px',
                  height: '32px'
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={editingId ? handleUpdate : handleCreate}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  color: '#8A95AA',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '6px'
                }}>
                  Shelf *
                </label>
                <select
                  required
                  value={formData.shelf_id}
                  onChange={(e) => setFormData({ ...formData, shelf_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                >
                  <option value="">Select shelf...</option>
                  {shelves.map(shelf => (
                    <option key={shelf.id} value={shelf.id}>
                      {shelf.name} ({shelf.location_name})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  color: '#8A95AA',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '6px'
                }}>
                  Position (auto-increments if blank)
                </label>
                <input
                  type="number"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder="1, 2, 3..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  color: '#8A95AA',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '6px'
                }}>
                  Nickname
                </label>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  placeholder="e.g., Ground Level, Eye Level"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  color: '#8A95AA',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '6px'
                }}>
                  Barcode (auto-generates if blank)
                </label>
                <input
                  type="text"
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  placeholder="e.g., T-001"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', paddingTop: '16px', borderTop: '1px solid rgba(255, 20, 147, 0.2)' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    border: '2px solid #6BCF7F',
                    background: 'rgba(107, 207, 127, 0.2)',
                    color: '#6BCF7F',
                    boxShadow: '0 0 15px rgba(107, 207, 127, 0.2)'
                  }}
                >
                  ✓ Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    border: '2px solid rgba(138, 149, 170, 0.4)',
                    background: 'rgba(138, 149, 170, 0.1)',
                    color: '#8A95AA'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
